import React from 'react';

import { withEventScope } from '@repo/event-processor';
import {
  Button,
  primarySmallTheme,
  primaryTheme,
  secondarySmallTheme,
  secondaryTheme,
} from '@repo/ui/components/Buttons';
import BackgroundIcon from '@repo/ui/components/Icon/BackgroundIcon';
import DownloadIcon from '@repo/ui/components/Icon/DownloadIcon';
import PricingCheckIcon from '@repo/ui/components/Icon/PricingCheckIcon';
import { themeStyle } from '@repo/ui/theming';

import ImageEditor from 'components/AiTools/ImageEditor/ImageEditor';
import { FlexRow } from 'styles/layouts';
import { LayoutSwitch } from 'styles/queries';

import { ButtonWrapper, Image as ImageComponents, Wrapper } from './styles';
import { SourceTemplateProps } from './types';

const SourceTemplate: React.FC<SourceTemplateProps> = (props) => {
  const iconDisabledColor = props.onRemoveBackground
    ? themeStyle.inkMain
    : themeStyle.inkMedium;

  const actions = (
    <FlexRow alignItems="center" gap="8px" style={{ height: '100%' }}>
      {props.stage === 'removingBackground' && !props.vectorizing ? (
        <LayoutSwitch
          desktopProps={{
            theme: { ...secondaryTheme, iconDisabledColor: iconDisabledColor },
            width: props.onRemoveBackground ? '200px' : '208px',
          }}
          mobileBreakpoint="largeTabletAndUp"
          mobileProps={{
            theme: {
              ...secondarySmallTheme,
              iconDisabledColor: iconDisabledColor,
            },
            width: 'auto',
          }}
        >
          <Button
            disabled={props.isLoading || !props.onRemoveBackground}
            icon={{
              component: props.onRemoveBackground
                ? BackgroundIcon
                : PricingCheckIcon,
            }}
            label={
              props.onRemoveBackground
                ? 'Remove Background'
                : 'Background Removed'
            }
            name="removeBackgroundButton"
            onClick={props.onRemoveBackground}
          />
        </LayoutSwitch>
      ) : (
        <Wrapper>
          <ButtonWrapper>
            <LayoutSwitch
              desktopHeight="40px"
              desktopProps={{
                theme: primaryTheme,
                width: props.isLoading ? '183px' : '161px',
              }}
              desktopWidth={props.isLoading ? '183px' : '161px'}
              height="100%"
              mobileBreakpoint="largeTabletAndUp"
              mobileHeight="33px"
              mobileProps={{
                theme: primarySmallTheme,
                width: '100%',
              }}
              mobileWidth="100%"
              width="100%"
            >
              <Button
                disabled={props.isLoading}
                icon={
                  !props.isLoading ? { component: DownloadIcon } : undefined
                }
                label={
                  !props.vectorizing ? 'Download SVG' : 'Vectorizing Image'
                }
                loading={props.vectorizing}
                name="sourceTemplateDownloadButton"
                onClick={props.onDownload}
              />
            </LayoutSwitch>
          </ButtonWrapper>
        </Wrapper>
      )}
    </FlexRow>
  );

  return (
    <LayoutSwitch
      desktopHeight="100%"
      height="100%"
      mobileBreakpoint="largeTabletAndUp"
      mobileHeight="100%"
      mobileProps={{ small: true, multiline: props.stage === 'vectorizing' }}
    >
      <ImageEditor
        additionActions={actions}
        background={props.background}
        isLoading={props.isLoading}
        isModified={props.isModified}
        onReplace={props.onReplace}
        onReset={props.onReset}
      >
        <ImageComponents alt="Image to vectorize" src={props.imageSrc} />
      </ImageEditor>
    </LayoutSwitch>
  );
};

export default withEventScope(SourceTemplate, { module: 'sourceTemplate' });
